"use client";

import {CSSProperties, JSX, useMemo} from "react";

import SmartImage from "@/shared/ui/smart-image";

import {useSnapshot} from "valtio";

import {ButtonBase} from "@/shared/ui/buttons/button-base";
import {Typography} from "@/shared/ui/data-display/typography";
import {ContainerBase} from "@/shared/ui/layout/container-base";
import {assetPath} from "@/shared/utils/asset-path";

import {VideoLibraryCard} from "./parts/video-library-card";
import {VideoLibraryFilters} from "./parts/video-library-filters";
import {VideoModal} from "./parts/video-modal";
import {type VideoItem, VideoLibraryModel} from "./video-library-list.model";

import "./styles/video-library-list.scss";

export interface VideoLibraryListProps {
    initialVideos: Array<VideoItem>;
    showFilters?: boolean;
    pageSize?: number;
    space?: CSSProperties;
    anchorId?: string;
}

export function VideoLibraryList({initialVideos, showFilters = true, pageSize = 12, space, anchorId}: VideoLibraryListProps): JSX.Element {
    // Create model with initial videos
    const model = useMemo(() => VideoLibraryModel(initialVideos, {pageSize}), [initialVideos, pageSize]);
    const snapshot = useSnapshot(model);

    return (
        <section className="video-library-section container container--padded" style={space} id={anchorId}>
            <ContainerBase>
                {showFilters && (
                    <VideoLibraryFilters location={model.location} category={model.category} subject={model.subject} search={model.search} />
                )}

                {snapshot.filteredVideos.length === 0 ? (
                    <div style={{textAlign: "center", padding: "3rem 0"}}>
                        <Typography as="p" size={16} color="paragraph">
                            No videos found
                        </Typography>
                    </div>
                ) : (
                    <>
                        <div className="video-library-section__grid">
                            {snapshot.visibleVideos.map((item) => (
                                <VideoLibraryCard key={item.id} item={item} libraryModel={model} modalModel={model.modalModel} />
                            ))}
                        </div>

                        {model.canLoadMore() && (
                            <div className="video-library-section__more">
                                <ButtonBase as="button" variant="secondary-outline" size="sm" width="auto" onClick={() => model.loadMore()}>
                                    Load more
                                    <SmartImage
                                        src={assetPath("/images/icons/arrow-black-down.svg")}
                                        alt="Arrow"
                                        width={12}
                                        height={10}
                                        style={{marginLeft: 8}}
                                    />
                                </ButtonBase>
                            </div>
                        )}
                    </>
                )}
            </ContainerBase>
            <VideoModal model={model.modalModel} />
        </section>
    );
}
